import { isDeviceServiceConfigured, isZvtConfigured } from "@/lib/deviceService";
import type { IntegrationsStatus } from "@/lib/integrationsStatusApi";
import type { InfoScreenEventsConfig } from "@/types/infoScreenEvents";
import type { TouristImportStats } from "@/lib/touristImportApi";
import type { FernausleseChannelList } from "@/lib/fernausleseApi";
import type { SchnittstellenIntegrationId } from "@/pages/stammdaten/schnittstellenIntegrations";

export interface IntegrationTileStatus {
  configured: boolean;
  detail: string;
}

export function buildIntegrationTileStatuses(
  settings: IntegrationsStatus,
  eventsConfig: InfoScreenEventsConfig | null,
  touristImportStats: TouristImportStats | null,
  fernausleseChannels: FernausleseChannelList | null,
): Record<SchnittstellenIntegrationId, IntegrationTileStatus> {
  const emailConfigured =
    settings.email.provider === "graph"
      ? Boolean(settings.email.from_address)
      : Boolean(settings.email.smtp_host && settings.email.smtp_password_configured);

  return {
    email: {
      configured: emailConfigured,
      detail: emailConfigured
        ? settings.email.provider === "graph"
          ? "Microsoft Graph"
          : `SMTP (${settings.email.smtp_host})`
        : "Nicht konfiguriert",
    },
    pms: {
      configured: Boolean(settings.integrations.pms_import_email),
      detail: settings.integrations.pms_import_email ?? "Keine Import-Adresse",
    },
    tourist: {
      configured: Boolean(settings.integrations.tourist_integration?.enabled),
      detail: settings.integrations.tourist_integration?.enabled
        ? "Aktiv (Verfügbarkeitsprüfung aus)"
        : "Deaktiviert",
    },
    "tourist-import": {
      configured: Boolean(
        touristImportStats &&
          (touristImportStats.completed_import_jobs > 0 ||
            touristImportStats.in_database.guests > 0 ||
            touristImportStats.in_database.bookings_total > 0),
      ),
      detail: touristImportStats
        ? touristImportStats.completed_import_jobs > 0
          ? `${touristImportStats.completed_import_jobs} Import(s), ${touristImportStats.in_database.guests.toLocaleString("de-DE")} Gäste`
          : "Noch kein Import durchgeführt"
        : "…",
    },
    "google-places": {
      configured: Boolean(settings.integrations.google_places_api_key_configured),
      detail: settings.integrations.google_places_api_key_configured
        ? "API-Key hinterlegt"
        : "Kein API-Key",
    },
    "channel-api": {
      configured: settings.integrations.api_keys.length > 0,
      detail:
        settings.integrations.api_keys.length > 0
          ? `${settings.integrations.api_keys.length} API-Key(s)`
          : "Keine API-Keys",
    },
    tse: {
      configured: settings.tse.enabled && settings.tse.configured,
      detail: settings.tse.enabled
        ? settings.tse.configured
          ? `Aktiv (${settings.tse.provider})`
          : "Aktiviert, unvollständig"
        : "Deaktiviert",
    },
    zvt: {
      configured: isZvtConfigured(settings.device_service),
      detail: settings.device_service?.enable_zvt ? "Aktiviert" : "Deaktiviert",
    },
    stripe: {
      configured: Boolean(settings.stripe?.configured),
      detail: !settings.stripe?.configured
        ? "Nicht konfiguriert"
        : !settings.stripe.webhook_secret_configured
          ? settings.stripe.mode === "test"
            ? "Testmodus, Webhook-Secret fehlt"
            : "Secret Key aktiv, Webhook-Secret fehlt"
          : settings.stripe.secret_key_source === "environment"
            ? settings.stripe.mode === "test"
              ? "Testmodus über Server-Umgebung"
              : "Über Server-Umgebung"
            : settings.stripe.mode === "test"
              ? "Testmodus, Zugang hinterlegt"
              : "Zugang hinterlegt",
    },
    "device-service": {
      configured: isDeviceServiceConfigured(settings.device_service),
      detail: settings.device_service?.base_url ?? "URL fehlt",
    },
    fernauslese: {
      configured: Boolean(
        (settings.fernauslese?.enabled &&
          settings.fernauslese.base_url &&
          settings.fernauslese.api_username &&
          settings.fernauslese.api_password_configured) ||
          (fernausleseChannels?.mapped_count ?? 0) > 0,
      ),
      detail: fernausleseChannels
        ? [
            settings.fernauslese?.enabled ? (settings.fernauslese.base_url ?? "API aktiv") : null,
            fernausleseChannels.catalog_count > 0
              ? `${fernausleseChannels.mapped_count}/${fernausleseChannels.catalog_count} Kanäle zugeordnet`
              : fernausleseChannels.mapped_count > 0
                ? `${fernausleseChannels.mapped_count} Kanäle zugeordnet`
                : null,
          ]
            .filter(Boolean)
            .join(" · ") || "Deaktiviert"
        : settings.fernauslese?.enabled
          ? (settings.fernauslese.base_url ?? "URL fehlt")
          : "Deaktiviert",
    },
    barrier: {
      configured: Boolean(settings.barrier?.enabled && settings.barrier.configured),
      detail: settings.barrier?.enabled
        ? settings.barrier.configured
          ? `Aktiv (${settings.barrier.provider})`
          : "Aktiviert, unvollständig"
        : "Deaktiviert",
    },
    lpr: {
      configured: Boolean(settings.lpr?.enabled && settings.lpr.configured),
      detail: settings.lpr?.enabled
        ? settings.lpr.configured
          ? `Aktiv (${settings.lpr.provider})`
          : "Aktiviert, API-Key fehlt"
        : "Deaktiviert",
    },
    events: {
      configured: Boolean(
        eventsConfig?.secrets_status.eventbrite_configured ||
          eventsConfig?.secrets_status.ticketmaster_configured,
      ),
      detail: eventsConfig
        ? [
            eventsConfig.secrets_status.eventbrite_configured ? "Eventbrite" : null,
            eventsConfig.secrets_status.ticketmaster_configured ? "Ticketmaster" : null,
            "Eventim (öffentlich)",
          ]
            .filter(Boolean)
            .join(", ") || "Nur Eventim (öffentlich)"
        : "…",
    },
  };
}
